import { useState } from 'react'
import type { Message } from '../../api/types'
import { ActionSheet, SheetMenu } from '../../components/ActionSheet'
import type { SheetRequest } from '../../hooks/gestures/useSheetTriggers'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { userName } from '../../lib/channels'
import { formatClock } from '../../lib/format'
import { MOBILE_QUERY, TOUCH_SCREEN_QUERY } from '../../lib/platform'
import { useConversation } from './ConversationContext'
import { EmojiPicker } from './EmojiPicker'
import { toggleReaction } from './messageActions'
import type { MessageRules } from './messageRules'
import { ReactionBar } from './ReactionBar'
import { replySummary } from './ReplyQuote'
import { useMessageMenu } from './useMessageMenu'

export interface MessageSheetRequest extends SheetRequest {
  view: 'actions' | 'emoji'
}

interface Props {
  message: Message
  rules: MessageRules
  request: MessageSheetRequest
  onClose: () => void
  onEdit: () => void
}

/**
 * Reactions and actions for a message (spec §3.3): a bottom sheet with a copy of the message on phones, a menu at the
 * pointer or the Actions button elsewhere.
 */
export function MessageSheet({ message, rules, request, onClose, onEdit }: Props) {
  const { me, users } = useConversation()
  const phone = useMediaQuery(MOBILE_QUERY)
  const touchScreen = useMediaQuery(TOUCH_SCREEN_QUERY)
  const [view, setView] = useState(request.view)
  // The actions lift the message where it sits; the emoji picker needs the height, so it slides up as a sheet.
  const lifted = (touchScreen || phone) && view === 'actions' && Boolean(request.anchor)
  const items = useMessageMenu(message, rules, onEdit)

  const react = (emoji: string) => {
    onClose()
    void toggleReaction(message, emoji, me.id)
  }

  return (
    <ActionSheet
      label="Message actions"
      request={request}
      onClose={onClose}
      anchor={view === 'actions' ? { element: request.anchor ?? null, side: message.authorId === me.id ? 'own' : 'other' } : null}
      view={view}
      // The first action, the picker's search (Back on touch screens), or + after coming back from the picker.
      focusIn={(panel, previousView) =>
        view === 'emoji'
          ? panel.querySelector<HTMLElement>('[data-autofocus]')
          : previousView === 'emoji'
            ? panel.querySelector<HTMLElement>('.more-reactions')
            : null
      }
    >
      {phone || lifted ? (
        <div className="sheet-message">
          <div>
            <strong>{message.authorId === me.id ? 'You' : userName(users, message.authorId)}</strong>
            <time dateTime={message.createdAt}>{formatClock(message.createdAt)}</time>
          </div>
          <p>{replySummary(message)}</p>
        </div>
      ) : null}

      {view === 'emoji' ? (
        <EmojiPicker message={message} onPick={react} onBack={() => setView('actions')} />
      ) : (
        <>
          {rules.react ? <ReactionBar message={message} className="reaction-bar" onReact={react} onMore={() => setView('emoji')} /> : null}
          <SheetMenu label="Actions" items={items} onClose={onClose} />
        </>
      )}
    </ActionSheet>
  )
}
